/**
 * The reader's time zone.
 *
 * The backend stores instants in UTC and treats an omitted zone as UTC.
 * This module is the other half: one IANA name for every report, used to
 * choose "today" and to print an instant. A civil day stays `YYYY-MM-DD`.
 *
 * `Intl.supportedValuesOf('timeZone')` omits `UTC` on Node 24. `UTC` is added
 * so the default the backend uses is a name the reader can pick.
 * An alias such as `MST` is refused. `Intl` would accept it and resolve it
 * to a different zone from the one that was typed.
 */

import { fromCalendarDate } from './calendar-date.ts'

/** The zone an omitted preference means on the server, and the fallback here. */
export const UTC_TIME_ZONE = 'UTC'

const ZONES = new Set(Intl.supportedValuesOf('timeZone'))
ZONES.add(UTC_TIME_ZONE)

/**
 * Whether `zone` is a canonical IANA name this browser can apply.
 *
 * A name the formatter rewrites, such as `MST`, is refused. The backend uses
 * the same rule, so a zone the select offers is a zone the API accepts.
 *
 * @param zone - A trimmed candidate.
 * @returns True when the name is canonical.
 */
function isCanonicalZone(zone: string): boolean {
  if (ZONES.has(zone)) {
    return true
  }
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions()
        .timeZone === zone
    )
  } catch {
    return false
  }
}

/** Zones the rail lists before the rest, in this order. */
const PREFERRED_ZONES = [
  UTC_TIME_ZONE,
  'America/Denver',
  'America/Chicago',
  'America/Los_Angeles',
  'America/New_York'
] as const

/** The closed rail control reads a city, not an IANA path. */
const ZONE_LABELS: Record<string, string> = {
  UTC: 'UTC',
  'America/Denver': 'Denver',
  'America/Chicago': 'Chicago',
  'America/Los_Angeles': 'Los Angeles',
  'America/New_York': 'New York'
}

/**
 * Read a zone name.
 *
 * @param value - A candidate name, from a URL or from storage.
 * @returns The name, or null when it is not one this console applies.
 */
export function parseTimeZone(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const zone = value.trim()

  return isCanonicalZone(zone) ? zone : null
}

/**
 * The zone the browser is set to.
 *
 * Falls back to UTC when the resolved name is not one this console applies.
 *
 * @returns An IANA time zone.
 */
export function browserTimeZone(): string {
  return (
    parseTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ??
    UTC_TIME_ZONE
  )
}

/**
 * Every zone the rail offers, preferred names first.
 *
 * @returns The options, in display order.
 */
export function timeZoneOptions(): readonly { value: string; label: string }[] {
  const preferred = PREFERRED_ZONES.filter(zone => ZONES.has(zone))
  const listed = new Set<string>(preferred)
  const rest = [...ZONES].filter(zone => !listed.has(zone))

  return [...preferred, ...rest].map(zone => ({
    value: zone,
    label: ZONE_LABELS[zone] ?? zone
  }))
}

/**
 * The civil day of an instant in a zone.
 *
 * Built from `formatToParts` so a locale cannot reorder the fields.
 *
 * @param timeZone - The IANA zone.
 * @param now - The instant. Defaults to the current one.
 * @returns The day, as `YYYY-MM-DD`.
 */
export function civilToday(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now)
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(part => part.type === type)?.value ?? ''

  return `${read('year')}-${read('month')}-${read('day')}`
}

/**
 * A civil day as the local-midnight `Date` the period arithmetic already uses.
 *
 * The `Date` is scratch. Its local fields are the civil day, so a week that
 * starts on Saturday does not move when the browser zone and the reader's
 * zone differ.
 *
 * @param timeZone - The IANA zone.
 * @param now - The instant. Defaults to the current one.
 * @returns That civil day at local midnight.
 */
export function zonedToday(timeZone: string, now: Date = new Date()): Date {
  return fromCalendarDate(civilToday(timeZone, now)) ?? new Date(now)
}

const instantFormatters = new Map<string, Intl.DateTimeFormat>()

/**
 * Print an instant in a zone.
 *
 * An unreadable value is returned unchanged, so a bad stamp stays visible.
 *
 * @param iso - An ISO-8601 instant.
 * @param timeZone - The IANA zone.
 * @returns The date and time to print.
 */
export function formatInstant(iso: string, timeZone: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) {
    return iso
  }

  let formatter = instantFormatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone
    })
    instantFormatters.set(timeZone, formatter)
  }

  return formatter.format(at)
}

/**
 * The sentence a report puts beside its freshness line.
 *
 * @param timeZone - The zone the report queried in.
 * @returns The sentence.
 */
export function reportingTimeZoneLabel(timeZone: string): string {
  return `Reporting time zone: ${timeZone}.`
}

/**
 * The UTC instant of civil midnight in `timeZone`.
 *
 * The guess is midnight UTC. The zone's offset at that instant moves it to
 * the midnight the reader meant, and a second look catches a guess that
 * landed on the other side of a clock change.
 *
 * @param civilDate - A `YYYY-MM-DD` day.
 * @param timeZone - The IANA zone.
 * @returns That midnight.
 */
function zonedMidnight(civilDate: string, timeZone: string): Date {
  const [year, month, day] = civilDate.split('-').map(Number)
  const guess = Date.UTC(year, month - 1, day)
  const offsetAt = (utcMs: number): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date(utcMs))
    const read = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find(part => part.type === type)?.value ?? '0')
    // A few engines write midnight as hour 24 of the day before.
    const hour = read('hour') === 24 ? 0 : read('hour')
    const civilDay = read('hour') === 24 ? read('day') + 1 : read('day')

    return (
      Date.UTC(
        read('year'),
        read('month') - 1,
        civilDay,
        hour,
        read('minute'),
        read('second')
      ) - utcMs
    )
  }
  const offset = offsetAt(guess)
  const corrected = offsetAt(guess - offset)

  return new Date(guess - (corrected === offset ? offset : corrected))
}

/**
 * The half-open UTC interval covering the civil days `from` through `to`.
 *
 * `to` is exclusive: it is midnight after the last day, so a timestamp on
 * that midnight belongs to the next day. The day is added on the calendar,
 * not by 24 hours, so a 23-hour or 25-hour day keeps its own length.
 *
 * @param from - The first civil day, `YYYY-MM-DD`.
 * @param to - The last civil day, `YYYY-MM-DD`.
 * @param timeZone - The reader's zone.
 * @returns `from` inclusive and `to` exclusive, as ISO-8601 UTC instants.
 */
export function utcInstantRange(
  from: string,
  to: string,
  timeZone: string
): { from: string; to: string } {
  const [year, month, day] = to.split('-').map(Number)
  const endDay = new Date(Date.UTC(year, month - 1, day + 1))
    .toISOString()
    .slice(0, 10)

  return {
    from: zonedMidnight(from, timeZone).toISOString(),
    to: zonedMidnight(endDay, timeZone).toISOString()
  }
}
